import { z } from 'zod'

export const RAF_COMPARISON_MONTHS = 3

// The server derives the trend from these rendered values rather than the raw
// scores, so the arrow can never claim a direction the two rendered numbers do
// not show.
export function formatRafScore(score: number): string {
  return score.toFixed(2)
}

export const rafScoreRequestSchema = z.object({
  patientId: z.string().trim().min(1),
  includeBreakdown: z.boolean().optional().nullable(),
})

export const rafDiagnosisSourceSchema = z.enum(['seen', 'external'])

export const rafDiagnosisSchema = z.object({
  code: z.string(),
  display: z.string().nullable(),
  sources: z.array(rafDiagnosisSourceSchema),
})

export const rafTermSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(['hcc', 'demographic', 'interaction']),
  coefficient: z.number(),
  diagnoses: z.array(rafDiagnosisSchema),
})

export const rafScoreSchema = z.object({
  score: z.number(),
  asOfMonth: z.string().date(),
  previousScore: z.number().nullable(),
  trend: z.enum(['up', 'down', 'flat']).nullable(),
  // The CMS-paid RAF for `asOfMonth`. Null for Medi-Cal-only participants and
  // months before CMS publishes the MMR.
  cmsScore: z.number().nullable(),
})

export const rafModelSchema = z.enum(['V22', 'V28'])

export const rafBreakdownSchema = z.object({
  model: rafModelSchema,
  modelScore: z.number(),
  terms: z.array(rafTermSchema),
})

export const rafScoreResponseSchema = z.object({
  rafScore: rafScoreSchema.nullable(),
  rafBreakdown: rafBreakdownSchema.nullable(),
  rafBreakdownV22: rafBreakdownSchema.nullable(),
})

export type RafScoreRequest = z.infer<typeof rafScoreRequestSchema>
export type RafScore = z.infer<typeof rafScoreSchema>
export type RafDiagnosisSource = z.infer<typeof rafDiagnosisSourceSchema>
export type RafDiagnosis = z.infer<typeof rafDiagnosisSchema>
export type RafTerm = z.infer<typeof rafTermSchema>
export type RafModel = z.infer<typeof rafModelSchema>
export type RafBreakdown = z.infer<typeof rafBreakdownSchema>
export type RafScoreResponse = z.infer<typeof rafScoreResponseSchema>
